import React, { useState, useEffect } from "react";
import Alert from "antd/es/alert";
import Empty from "antd/es/empty";
import Table from "antd/es/table";
import Button from "antd/es/button";
import { DownloadOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import * as Sentry from "@sentry/react";
import axios from "axios";
import dayjs from "dayjs";

import { genericError, logOut } from "./Dashboard";

const { REACT_APP_API_URL: API_URL } = process.env;

export default function InvoiceList({ landlord, rental, period }) {
  const intl = useIntl();
  const [invoices, setInvoices] = useState([]);

  async function downloadInvoicePDF(id) {
    Sentry.logger.debug("Invoice download started", { invoiceId: id });

    await axios({
      method: "get",
      url: `${API_URL}/invoices/download-invoice-pdf-portal/${id}`,
      headers: { Authorization: `Bearer ${localStorage.token}` },
      responseType: "blob",
    })
      .then((response) => {
        if (response.status === 200) {
          const url = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `invoice-${id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          Sentry.logger.info("Invoice downloaded", { invoiceId: id });
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          contexts: {
            custom: { message: "Invoice download failed", invoiceId: id },
          },
        });
        Sentry.logger.error("Invoice download failed", {
          invoiceId: id,
          error: error.message,
        });
        genericError(intl);
      });
  }

  async function getInvoices({ landlord, rental, period }) {
    Sentry.logger.debug("Loading invoices", {
      rentalId: rental.bookingsync_rental_id,
      landlordId: landlord.id,
      period: period.toString(),
    });

    await axios(
      {
        method: "get",
        url: `${API_URL}/invoices/list-invoices?rental_id=${
          rental.bookingsync_rental_id
        }&landlord_id=${landlord.id}&period=${period.toString()}`,
        headers: { Authorization: `Bearer ${localStorage.token}` },
      },
      []
    )
      .then((response) => {
        if (response.status === 200) {
          setInvoices(response.data);
          Sentry.logger.debug("Invoices loaded", {
            count: response.data.length,
            rentalId: rental.bookingsync_rental_id,
          });
        } else {
          setInvoices([]);
        }
      })
      .catch((error) =>
        error.response && error.response.status && error.response.status === 401
          ? logOut(intl)
          : genericError(intl)
      );
  }

  useEffect(() => {
    if (!landlord || !rental || !period) {
      return;
    }

    getInvoices({ landlord, rental, period });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landlord, rental, period]);

  if (!landlord || !rental || !period || !invoices || invoices.length === 0) {
    return (
      <Empty
        className="empty-sign"
        description={intl.formatMessage({ id: "no-invoices" })}
      />
    );
  }

  const showKsefWarning =
    landlord.nip && period.toDate().isAfter(dayjs("2026-01-01"));

  return (
    <div style={{ maxWidth: 750 }}>
      <br />
      {showKsefWarning && (
        <Alert
          message={intl.formatMessage({ id: "invoice-ksef-warning" })}
          type="warning"
          showIcon
          style={{ marginBottom: 16, whiteSpace: "pre-line" }}
        />
      )}
      <Table dataSource={invoices} rowKey="id" size="large" pagination={false}>
        <Table.Column
          title={intl.formatMessage({ id: "invoice-number" })}
          dataIndex="number"
        />
        <Table.Column
          title={intl.formatMessage({ id: "invoice-download-column" })}
          dataIndex="id"
          render={(value) => (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => downloadInvoicePDF(value)}
            >
              {intl.formatMessage({ id: "invoice-download-button" })}
            </Button>
          )}
        />
      </Table>
    </div>
  );
}
